/**
 * Supervisor task calendar model (#3671).
 *
 * Pure helpers: which tasks are drawn where, and how instants relate to the
 * TENANT's wall clock. Nothing here reads the browser time zone to decide a day,
 * an hour or a snap. The timeline widget takes real epoch instants (bounds,
 * bars, drop positions), so the picture is placed by actual elapsed time: a
 * tenant day is the span from its own midnight to the next one (23 or 25 hours
 * on DST days), a bar always has a positive duration, and a drop keeps the
 * instant it was released at, including which of two repeated autumn hours it is.
 * Wall-clock text (header labels, dialogs) is formatted in the tenant zone from
 * those instants. No browser-local `Date` is ever made to "look like" the tenant.
 * The header cells and grid lines are built here too (see "Tenant-zone grid"),
 * because the widget would cut them at the browser's midnight.
 */

import type { GridLine, HeaderCell, TimeUnit } from 'mq-timeline-calendar/core';
import { enUS, fiFI, svSE, type CalendarLocale } from 'mq-timeline-calendar/react';
import type { TaskAudience, TaskListItem } from '@/hooks/useTasks';

/** The server's tenant timezone default (packages/shared tenant schema). */
export const FALLBACK_TENANT_TIME_ZONE = 'Europe/Helsinki';

/** Bar length for a scheduled task without an estimate. */
export const DEFAULT_TASK_MINUTES = 60;
/** Shortest bar, so a 0 h estimate stays clickable. */
export const MIN_TASK_MINUTES = 15;
/** A dropped task lands on a quarter hour. */
export const DRAG_SNAP_MINUTES = 15;

export type SupervisorCalendarView = 'day' | 'week';

const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatters = new Map<string, Intl.DateTimeFormat>();

function zoneFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}

/** A usable IANA zone: the tenant's when valid, otherwise the server default. */
export function resolveTenantTimeZone(value: string | null | undefined): string {
  if (!value) return FALLBACK_TENANT_TIME_ZONE;
  try {
    zoneFormatter(value);
    return value;
  } catch {
    return FALLBACK_TENANT_TIME_ZONE;
  }
}

/** Offset (zone wall clock minus UTC) in force at `ms`. */
export function tenantOffsetMs(ms: number, timeZone: string): number {
  const parts = zoneFormatter(timeZone).formatToParts(new Date(ms));
  const num = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  const wallAsUtc = Date.UTC(
    num('year'),
    num('month') - 1,
    num('day'),
    num('hour'),
    num('minute'),
    num('second'),
  );
  // Whole seconds only: the formatter drops milliseconds.
  return wallAsUtc - Math.floor(ms / 1000) * 1000;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** `YYYY-MM-DD` + whole days, by calendar arithmetic (no zone involved). */
export function addDaysToDate(date: string, days: number): string {
  const match = DATE_RE.exec(date);
  if (!match) throw new Error(`Invalid calendar date: ${date}`);
  const moved = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days));
  return `${String(moved.getUTCFullYear())}-${pad(moved.getUTCMonth() + 1)}-${pad(moved.getUTCDate())}`;
}

/** Monday of the ISO week containing `date`. */
export function startOfWeekDate(date: string): string {
  const match = DATE_RE.exec(date);
  if (!match) throw new Error(`Invalid calendar date: ${date}`);
  const weekday = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  ).getUTCDay();
  return addDaysToDate(date, -((weekday + 6) % 7));
}

/** The tenant's wall-clock date and minutes-since-midnight at an instant. */
export function instantToTenantWall(
  instant: string | number | Date,
  timeZone: string,
): { date: string; minutes: number } | null {
  const ms = instant instanceof Date ? instant.getTime() : new Date(instant).getTime();
  if (!Number.isFinite(ms)) return null;
  const wall = new Date(ms + tenantOffsetMs(ms, timeZone));
  return {
    date: `${String(wall.getUTCFullYear())}-${pad(wall.getUTCMonth() + 1)}-${pad(wall.getUTCDate())}`,
    minutes: wall.getUTCHours() * 60 + wall.getUTCMinutes(),
  };
}

/** Today's date in the tenant zone. */
export function tenantToday(now: Date, timeZone: string): string {
  return instantToTenantWall(now, timeZone)?.date ?? '1970-01-01';
}

/**
 * The instant at which the tenant's wall clock reads `date` + `minutes`
 * (`minutes` may exceed 1440 to name a later day).
 *
 * A repeated autumn hour resolves to its FIRST occurrence; a skipped spring
 * hour resolves forward (03:30 on the spring-forward day becomes 04:30), the
 * same "compatible" choice the Temporal API makes.
 */
export function tenantWallToInstant(date: string, minutes: number, timeZone: string): Date {
  const match = DATE_RE.exec(date);
  if (!match) throw new Error(`Invalid calendar date: ${date}`);
  const guess = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 0, minutes);
  const before = tenantOffsetMs(guess - DAY_MS, timeZone);
  const after = tenantOffsetMs(guess + DAY_MS, timeZone);
  const valid = [...new Set([before, after])]
    .map((offset) => guess - offset)
    .filter((candidate) => tenantOffsetMs(candidate, timeZone) === guess - candidate);
  if (valid.length > 0) return new Date(Math.min(...valid));
  return new Date(guess - before);
}

/**
 * First and last tenant day shown, and the real instants bounding them.
 * `from`/`to` are the inclusive pair sent to the API (`to` is 1 ms before the
 * next midnight); `endExclusive` is that next tenant midnight, which is the
 * timeline's right edge. DST days are 23 or 25 hours long.
 */
export function viewWindow(
  anchorDate: string,
  view: SupervisorCalendarView,
  timeZone: string,
): { firstDay: string; lastDay: string; from: Date; to: Date; endExclusive: Date } {
  const firstDay = view === 'week' ? startOfWeekDate(anchorDate) : anchorDate;
  const lastDay = view === 'week' ? addDaysToDate(firstDay, 6) : anchorDate;
  const from = tenantWallToInstant(firstDay, 0, timeZone);
  const endExclusive = tenantWallToInstant(addDaysToDate(lastDay, 1), 0, timeZone);
  return { firstDay, lastDay, from, to: new Date(endExclusive.getTime() - 1), endExclusive };
}

/**
 * Snap a real instant to the nearest quarter hour of the TENANT's clock without
 * leaving the timeline: the result is the same instant shifted by whole minutes,
 * so a drop on the second of two repeated autumn hours stays in the second one
 * (going through wall-clock text would collapse it onto the first).
 */
export function snapInstantToTenantGrid(
  instantMs: number,
  timeZone: string,
  snapMinutes: number = DRAG_SNAP_MINUTES,
): number {
  const snapMs = snapMinutes * 60_000;
  const offset = tenantOffsetMs(instantMs, timeZone);
  return Math.round((instantMs + offset) / snapMs) * snapMs - offset;
}

/** Offset text for a zone offset in ms: `+3`, `+5:30`, `-4`, `+0`. */
function formatOffset(offsetMs: number): string {
  const totalMinutes = Math.round(offsetMs / 60_000);
  const sign = totalMinutes < 0 ? '-' : '+';
  const abs = Math.abs(totalMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  return `${sign}${String(hours)}${minutes === 0 ? '' : `:${pad(minutes)}`}`;
}

/** `+3`-style UTC offset of the tenant zone at an instant. */
export function tenantOffsetLabel(instantMs: number, timeZone: string): string {
  return formatOffset(tenantOffsetMs(instantMs, timeZone));
}

/** Folds in use today: 60 minutes almost everywhere, 30 minutes at Lord Howe Island. */
const FOLD_SHIFTS_MS = [-3_600_000, -1_800_000, 1_800_000, 3_600_000];

/**
 * True when the tenant's wall clock shows this moment twice (the repeated hour
 * when DST ends): the same wall time exists one fold earlier or later.
 */
export function isRepeatedTenantHour(instantMs: number, timeZone: string): boolean {
  const here = instantToTenantWall(instantMs, timeZone);
  if (!here) return false;
  return FOLD_SHIFTS_MS.some((shift) => {
    const other = instantToTenantWall(instantMs + shift, timeZone);
    return other?.date === here.date && other.minutes === here.minutes;
  });
}

/**
 * The timeline's own locale for the UI language: Finnish and Swedish where the
 * library ships them, English (also the fallback) otherwise.
 */
export function calendarLocaleFor(language: string): CalendarLocale {
  const base = language.toLowerCase().split('-')[0];
  if (base === 'fi') return fiFI;
  if (base === 'sv') return svSE;
  return enUS;
}

export interface SupervisorTimedEvent {
  kind: 'timed';
  id: string;
  task: TaskListItem;
  /** Real instants; `endMs - startMs` is always positive. */
  startMs: number;
  endMs: number;
}

export interface SupervisorAllDayEvent {
  kind: 'allDay';
  id: string;
  task: TaskListItem;
  /** Tenant calendar date of the deadline. */
  date: string;
  /** Real instants of that tenant day's two midnights (23 or 25 h apart on DST days). */
  startMs: number;
  endMs: number;
}

export type SupervisorTaskEvent = SupervisorTimedEvent | SupervisorAllDayEvent;

export interface BuildEventsOptions {
  timeZone: string;
  /** Also show tasks whose audience is "anyone" (default: supervisors only). */
  includeAnyone: boolean;
}

function audienceVisible(audience: TaskAudience | undefined, includeAnyone: boolean): boolean {
  // A task that predates the audience column reads as 'anyone'.
  const value = audience ?? 'anyone';
  return value === 'supervisors' || (includeAnyone && value === 'anyone');
}

/**
 * Duration in minutes a scheduled task is DRAWN for. The API serialises numeric as
 * a string. No estimate, or one of zero or less, is DEFAULT_TASK_MINUTES; a positive
 * estimate is rounded to whole minutes and floored at MIN_TASK_MINUTES.
 * staff-api's `calendarBarMinutesSql` (routes/tasks.ts) repeats this rule so the
 * window fetch returns exactly the bars that reach into the window; change both together.
 */
export function taskDurationMinutes(estimatedDurationHours: number | string | null): number {
  const hours = estimatedDurationHours === null ? Number.NaN : Number(estimatedDurationHours);
  if (!Number.isFinite(hours) || hours <= 0) return DEFAULT_TASK_MINUTES;
  return Math.max(MIN_TASK_MINUTES, Math.round(hours * 60));
}

/**
 * Split tasks into timed events (scheduledAt + estimate) and all-day deadline
 * events (no schedule, deadline set). A scheduled task is always timed — its
 * deadline does not add a second, all-day entry. Cancelled tasks and tasks
 * with neither field are not drawn.
 */
export function buildSupervisorTaskEvents(
  tasks: readonly TaskListItem[],
  { timeZone, includeAnyone }: BuildEventsOptions,
): SupervisorTaskEvent[] {
  const seen = new Set<string>();
  const events: SupervisorTaskEvent[] = [];
  for (const task of tasks) {
    if (seen.has(task.id)) continue;
    seen.add(task.id);
    if (task.status === 'cancelled') continue;
    if (!audienceVisible(task.audience, includeAnyone)) continue;

    if (task.scheduledAt) {
      const startMs = new Date(task.scheduledAt).getTime();
      if (!Number.isFinite(startMs)) continue;
      const endMs = startMs + taskDurationMinutes(task.estimatedDurationHours) * 60_000;
      events.push({ kind: 'timed', id: task.id, task, startMs, endMs });
      continue;
    }

    if (task.deadline) {
      const wall = instantToTenantWall(task.deadline, timeZone);
      if (!wall) continue;
      events.push({
        kind: 'allDay',
        id: task.id,
        task,
        date: wall.date,
        startMs: tenantWallToInstant(wall.date, 0, timeZone).getTime(),
        endMs: tenantWallToInstant(addDaysToDate(wall.date, 1), 0, timeZone).getTime(),
      });
    }
  }
  return events.sort((a, b) => a.startMs - b.startMs);
}

/** Greedy lane packing so overlapping bars sit on separate timeline rows. */
export function assignLanes<T extends { startMs: number; endMs: number }>(
  events: readonly T[],
): { lanes: Array<{ event: T; lane: number }>; laneCount: number } {
  const laneEnds: number[] = [];
  const lanes: Array<{ event: T; lane: number }> = [];
  for (const event of [...events].sort((a, b) => a.startMs - b.startMs)) {
    let lane = laneEnds.findIndex((end) => end <= event.startMs);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = event.endMs;
    lanes.push({ event, lane });
  }
  return { lanes, laneCount: Math.max(1, laneEnds.length) };
}

export type ReschedulePlan =
  | { kind: 'noop' }
  | { kind: 'move'; scheduledAt: string; expectedUpdatedAt: string };

/**
 * A drop → what to send. `droppedAtMs` is the real instant the timeline reports.
 * It is snapped to the tenant's quarter hour as an instant (fold-preserving), and
 * a drop that lands on the task's current instant — or snaps back onto it — is a
 * no-op, so touching a bar on the second repeated hour never moves it to the
 * first. The expected `updatedAt` is the one the calendar rendered, so a
 * colleague's edit in between comes back as a 412 rather than being overwritten.
 */
export function planReschedule(
  task: Pick<TaskListItem, 'scheduledAt' | 'updatedAt'>,
  droppedAtMs: number,
  timeZone: string,
): ReschedulePlan {
  if (!task.scheduledAt || !Number.isFinite(droppedAtMs)) return { kind: 'noop' };
  const currentMs = new Date(task.scheduledAt).getTime();
  if (!Number.isFinite(currentMs) || droppedAtMs === currentMs) return { kind: 'noop' };
  const nextMs = snapInstantToTenantGrid(droppedAtMs, timeZone);
  if (nextMs === currentMs) return { kind: 'noop' };
  return {
    kind: 'move',
    scheduledAt: new Date(nextMs).toISOString(),
    expectedUpdatedAt: task.updatedAt,
  };
}

/** A tenant calendar date for display, in `locale`. Anchored to UTC so the browser zone cannot shift the day. */
export function formatTenantDate(
  date: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string {
  const match = DATE_RE.exec(date);
  if (!match) return date;
  const anchor = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12));
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(anchor);
}

/** An instant for display in the tenant zone (date and time), in `locale`. */
export function formatTenantInstant(instant: string, locale: string, timeZone: string): string {
  const ms = new Date(instant).getTime();
  if (!Number.isFinite(ms)) return instant;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  }).format(new Date(ms));
}

// ---------------------------------------------------------------------------
// Tenant-zone grid
//
// The timeline widget cuts its own header cells and grid lines with the browser's
// local `setHours()`, so a day starts at the BROWSER's midnight and, in a browser
// that observes the same DST change, one of the two repeated autumn hours gets no
// cell at all. The grid below is computed from real instants instead:
//
// - a day is the span between two TENANT midnights (23, 24 or 25 real hours),
// - clock cells and lines sit at instants whose tenant wall clock is on the step
//   (so a 30 or 45 minute offset zone is aligned to its own hours),
// - both occurrences of a repeated hour get their own cell, and the instant at
//   which the offset changes is itself a cell boundary.
//
// Positions are pixels from the viewport start on a linear axis (the calendar
// never compresses closed hours), the same convention as the widget's own cells.
// ---------------------------------------------------------------------------

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
/** The offset is sampled this often; a DST change is never closer to another one. */
const OFFSET_SAMPLE_MS = 6 * HOUR_MS;
const MAX_DAY_SEGMENTS = 400;

/** A tenant calendar day as real time: its own midnight up to the next one. */
export interface TenantDaySegment {
  /** Tenant calendar date, `YYYY-MM-DD`. */
  date: string;
  startMs: number;
  endMs: number;
  /** Real length in hours: 23 or 25 on DST days. */
  hours: number;
}

/**
 * The tenant days covering `[startMs, endMs)`, contiguous, each from its own
 * midnight to the next. The first segment starts at the midnight of the tenant
 * date `startMs` falls on.
 */
export function tenantDaySegments(
  startMs: number,
  endMs: number,
  timeZone: string,
): TenantDaySegment[] {
  const first = instantToTenantWall(startMs, timeZone);
  if (!first || !Number.isFinite(endMs) || endMs <= startMs) return [];
  const segments: TenantDaySegment[] = [];
  let date = first.date;
  let segmentStart = tenantWallToInstant(date, 0, timeZone).getTime();
  while (segmentStart < endMs && segments.length < MAX_DAY_SEGMENTS) {
    const nextDate = addDaysToDate(date, 1);
    const segmentEnd = tenantWallToInstant(nextDate, 0, timeZone).getTime();
    segments.push({
      date,
      startMs: segmentStart,
      endMs: segmentEnd,
      hours: Number(((segmentEnd - segmentStart) / HOUR_MS).toFixed(2)),
    });
    date = nextDate;
    segmentStart = segmentEnd;
  }
  return segments;
}

/** A stretch of real time in which the tenant zone's UTC offset is constant. */
export interface OffsetSegment {
  fromMs: number;
  toMs: number;
  offsetMs: number;
}

/**
 * Split `[startMs, endMs)` where the tenant zone's UTC offset changes (DST). The
 * offset is sampled every few hours and each change located to the millisecond
 * by bisection, so the cost does not depend on the length of the window.
 */
export function tenantOffsetSegments(
  startMs: number,
  endMs: number,
  timeZone: string,
): OffsetSegment[] {
  if (!(endMs > startMs)) return [];
  const segments: OffsetSegment[] = [];
  let from = startMs;
  let offset = tenantOffsetMs(startMs, timeZone);
  let cursor = startMs;
  while (cursor < endMs) {
    const probe = Math.min(cursor + OFFSET_SAMPLE_MS, endMs);
    const probeOffset = tenantOffsetMs(probe, timeZone);
    if (probeOffset === offset) {
      cursor = probe;
      continue;
    }
    // The offset is `offset` at `lo` and different at `hi`: find the first instant that differs.
    let lo = cursor;
    let hi = probe;
    while (hi - lo > 1) {
      const mid = Math.floor((lo + hi) / 2);
      if (tenantOffsetMs(mid, timeZone) === offset) lo = mid;
      else hi = mid;
    }
    segments.push({ fromMs: from, toMs: hi, offsetMs: offset });
    from = hi;
    offset = probeOffset;
    cursor = hi;
  }
  if (endMs > from) segments.push({ fromMs: from, toMs: endMs, offsetMs: offset });
  return segments;
}

/** Clock steps the header can show, in minutes. Every one divides a day. */
export const CLOCK_STEPS_MINUTES = [15, 30, 60, 120, 180, 360, 720] as const;

/** Narrowest a labelled cell may be: `HH` needs less room than `HH:MM`. */
function minCellPx(stepMinutes: number): number {
  if (stepMinutes >= 60 && stepMinutes < 360) return 28;
  return 44;
}

/** The finest clock step whose cells are wide enough to read, or null when even 12 h is not. */
export function chooseClockStep(pixelsPerMs: number): number | null {
  if (!(pixelsPerMs > 0)) return null;
  for (const step of CLOCK_STEPS_MINUTES) {
    if (step * MINUTE_MS * pixelsPerMs >= minCellPx(step)) return step;
  }
  return null;
}

function clockUnit(stepMinutes: number): TimeUnit {
  if (stepMinutes <= 15) return 'quarterhour';
  if (stepMinutes <= 30) return 'halfhour';
  if (stepMinutes <= 180) return 'hour';
  if (stepMinutes <= 360) return 'quarterday';
  return 'halfday';
}

/**
 * Instants in `[startMs, endMs)` at which the tenant wall clock is on a
 * `stepMinutes` boundary, plus the instant of every offset change (the spring
 * gap and the autumn fold are cell boundaries whatever the step). Sorted, each
 * real instant once, so a repeated hour yields two marks.
 */
export function tenantClockMarks(
  startMs: number,
  endMs: number,
  stepMinutes: number,
  timeZone: string,
): number[] {
  const stepMs = stepMinutes * MINUTE_MS;
  const marks = new Set<number>();
  tenantOffsetSegments(startMs, endMs, timeZone).forEach((segment, index) => {
    if (index > 0) marks.add(segment.fromMs);
    // Wall time = real time + offset, and the step divides a day: aligned wall time <=> aligned (t + offset).
    for (let k = Math.ceil((segment.fromMs + segment.offsetMs) / stepMs); ; k += 1) {
      const ms = k * stepMs - segment.offsetMs;
      if (ms >= segment.toMs) break;
      if (ms >= segment.fromMs) marks.add(ms);
    }
  });
  return [...marks].sort((a, b) => a - b);
}

export type GridLabelLocale = Pick<CalendarLocale, 'weekdaysShort'>;

export interface DescribedHeaderCell {
  /** The cell's visible text. */
  text: string;
  /** Fuller text for the tooltip and assistive tech; names the UTC offset of a clock cell. */
  title: string;
  /** Tenant calendar date the cell starts on. */
  date: string;
  /** A clock cell inside a repeated hour, or at an offset change. */
  offsetShown: boolean;
  /** Real length of a day cell in hours (23 / 24 / 25); null for clock cells. */
  hours: number | null;
}

/**
 * Text for a header cell that starts at `timestampMs`, in the tenant's clock.
 * Day cells name the tenant date they start on (the cell IS a tenant day, so no
 * guess about which day it is "mostly" in); clock cells name the wall time and,
 * inside a repeated hour or at an offset change, the UTC offset that tells the
 * two occurrences apart (`03 (+3)` / `03 (+2)`).
 */
export function describeHeaderCell(
  cell: { timestamp: number; type: string },
  locale: GridLabelLocale,
  timeZone: string,
  language: string,
): DescribedHeaderCell | null {
  const wall = instantToTenantWall(cell.timestamp, timeZone);
  if (!wall) return null;

  if (cell.type === 'day') {
    const [year, month, day] = wall.date.split('-').map(Number) as [number, number, number];
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    const text = `${locale.weekdaysShort[weekday] ?? ''} ${String(day)}`.trim();
    const nextMidnight = tenantWallToInstant(addDaysToDate(wall.date, 1), 0, timeZone).getTime();
    const hours = Number(((nextMidnight - cell.timestamp) / HOUR_MS).toFixed(2));
    const date = formatTenantDate(wall.date, language, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    return {
      text,
      title: hours === 24 ? date : `${date} · ${String(hours)} h`,
      date: wall.date,
      offsetShown: false,
      hours,
    };
  }

  const hh = Math.floor(wall.minutes / 60);
  const mm = wall.minutes % 60;
  const clock = `${pad(hh)}:${pad(mm)}`;
  const offset = tenantOffsetLabel(cell.timestamp, timeZone);
  const offsetChanged =
    tenantOffsetMs(cell.timestamp, timeZone) !== tenantOffsetMs(cell.timestamp - 1, timeZone);
  const offsetShown = offsetChanged || isRepeatedTenantHour(cell.timestamp, timeZone);
  const base = cell.type === 'hour' && mm === 0 ? pad(hh) : clock;
  return {
    text: offsetShown ? `${base} (${offset})` : base,
    title: `${clock} UTC${offset}`,
    date: wall.date,
    offsetShown,
    hours: null,
  };
}

export interface TenantGridInput {
  /** The instants at the viewport's edges. */
  viewStartMs: number;
  viewEndMs: number;
  pixelsPerMs: number;
  /** The fetched window: the grid never reaches outside it. */
  windowStartMs: number;
  windowEndMs: number;
  timeZone: string;
  locale: GridLabelLocale;
  /** UI language (BCP 47) for the long date in titles. */
  language: string;
}

export interface TenantGrid {
  /** Row 0: tenant days. Row 1 (when the zoom allows): clock cells. */
  headerCells: HeaderCell[][];
  /** Tenant day boundaries (primary) and clock lines. */
  gridLines: GridLine[];
  /** Clock step in minutes, or null when only days fit. */
  stepMinutes: number | null;
}

/**
 * Header cells and grid lines for the viewport, from tenant days and tenant
 * clock marks. Cells are clipped to the viewport like the widget's own.
 */
export function buildTenantGrid(input: TenantGridInput): TenantGrid {
  const { viewStartMs, viewEndMs, pixelsPerMs, windowStartMs, windowEndMs, timeZone, locale } = input;
  const at = (ms: number): number => (ms - viewStartMs) * pixelsPerMs;
  const headerCells: HeaderCell[][] = [];
  const gridLines: GridLine[] = [];

  const cell = (
    startMs: number,
    endMs: number,
    type: TimeUnit,
    level: number,
    isPrimary: boolean,
  ): HeaderCell | null => {
    const from = Math.max(startMs, viewStartMs);
    const to = Math.min(endMs, viewEndMs);
    if (!(to > from)) return null;
    const described = describeHeaderCell({ timestamp: startMs, type }, locale, timeZone, input.language);
    if (!described) return null;
    return {
      timestamp: startMs,
      position: at(from),
      width: (to - from) * pixelsPerMs,
      type,
      label: described.text,
      isPrimary,
      level,
      isPartiallyVisible: startMs < viewStartMs || endMs > viewEndMs,
    };
  };

  const days = tenantDaySegments(windowStartMs, windowEndMs, timeZone);
  const dayRow: HeaderCell[] = [];
  for (const day of days) {
    const dayCell = cell(day.startMs, Math.min(day.endMs, windowEndMs), 'day', 0, true);
    if (dayCell) dayRow.push(dayCell);
    if (day.startMs >= viewStartMs && day.startMs < viewEndMs) {
      gridLines.push({
        timestamp: day.startMs,
        position: at(day.startMs),
        type: 'day',
        label: '',
        isPrimary: true,
        level: 0,
      });
    }
  }
  headerCells.push(dayRow);

  const stepMinutes = chooseClockStep(pixelsPerMs);
  if (stepMinutes !== null) {
    const type = clockUnit(stepMinutes);
    const dayStarts = new Set(days.map((day) => day.startMs));
    const marks = tenantClockMarks(windowStartMs, windowEndMs, stepMinutes, timeZone);
    if (marks[0] !== windowStartMs) marks.unshift(windowStartMs);
    const clockRow: HeaderCell[] = [];
    marks.forEach((startMs, index) => {
      const endMs = marks[index + 1] ?? windowEndMs;
      if (endMs <= viewStartMs || startMs >= viewEndMs) return;
      const clockCell = cell(startMs, endMs, type, 1, false);
      if (clockCell) clockRow.push(clockCell);
      if (startMs >= viewStartMs && !dayStarts.has(startMs)) {
        gridLines.push({
          timestamp: startMs,
          position: at(startMs),
          type,
          label: '',
          isPrimary: false,
          level: 1,
        });
      }
    });
    headerCells.push(clockRow);
  }

  gridLines.sort((a, b) => a.timestamp - b.timestamp);
  return { headerCells, gridLines, stepMinutes };
}

export type ViewportCorrection =
  | { kind: 'fit' }
  | { kind: 'shift'; deltaMs: number }
  | null;

/** Slack for the floating-point width of a fitted viewport. */
const VIEWPORT_EPSILON_MS = 1_000;

/**
 * What it takes to bring a panned or zoomed viewport back inside the fetched
 * window: the widget lets the user move anywhere, but tasks are only loaded for
 * the selected day or week, so the grid must not show time that was not fetched.
 * Wider than the window → refit to it; past an edge → shift back by that much.
 */
export function viewportCorrection(
  viewStartMs: number,
  viewEndMs: number,
  windowStartMs: number,
  windowEndMs: number,
): ViewportCorrection {
  if (viewEndMs - viewStartMs > windowEndMs - windowStartMs + VIEWPORT_EPSILON_MS) {
    return { kind: 'fit' };
  }
  if (viewStartMs < windowStartMs - VIEWPORT_EPSILON_MS) {
    return { kind: 'shift', deltaMs: windowStartMs - viewStartMs };
  }
  if (viewEndMs > windowEndMs + VIEWPORT_EPSILON_MS) {
    return { kind: 'shift', deltaMs: windowEndMs - viewEndMs };
  }
  return null;
}
